import { EnumTipoOnereDTOTipoVincolo } from '@framework/api-client/api/inquiries/schemas/enumTipoOnereDTOTipoVincolo.ts';
import { useGetFrontRichiesteIdPreventivi } from '@framework/api-client/api/inquiries-compile/preventivi-richiesta/preventivi-richiesta.ts';
import {
  useGetFrontRichiesteIdOneri,
  useGetFrontRichiesteIdPagamenti,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { EnumRichiestaDTOStato } from '@framework/api-client/api/inquiries-compile/schemas/enumRichiestaDTOStato.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { type ErrorType } from '@framework/api-client/client/apiClient.ts';
import { ResponsiveCard } from '@framework/ui/components/card/ResponsiveCard.tsx';
import { LoaderSpin } from '@framework/ui/components/loader/LoaderSpin.tsx';
import { Alert, Divider, message } from 'antd';
import Paragraph from 'antd/es/typography/Paragraph';
import Title from 'antd/es/typography/Title';
import qs from 'qs';
import type React from 'react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { CompilationContainer } from '../burden/CompilationContainer.tsx';
import { SubmissionBurdenCardFooter } from '../burden/SubmissionBurdenCardFooter.tsx';
import { type ISubmissionNavigator } from '../navigation/useSubmissionNavigation.ts';
import { useGenerateAutomaticBurdenPayments } from '../utils/useGenerateAutomaticBurdenPayments.ts';
import { BurdenSelectionTable } from './BurdenSelectionTable.tsx';
import { PaymentsTable } from './PaymentsTable.tsx';
import { type ISubmissionQuote } from './quote/ISubmissionQuote.ts';
import { QuoteTable } from './quote/QuoteTable.tsx';
import { getValidPayments } from './utils/getValidPayments.ts';

const StyledResponsiveCard = styled(ResponsiveCard)`
  border: 1px solid ${props => props.theme.colorSplit};
  font-size: 18px;
`;

/**
 * Wrapper di utility per permettere di nascondere la card in base a una condizione
 */
function StyledResponsiveCardWrapper(props: {
  children: React.ReactNode;
  hideCard: boolean;
}) {
  if (props.hideCard) return <>{props.children}</>;
  return <StyledResponsiveCard>{props.children}</StyledResponsiveCard>;
}

interface SubmissionPaymentsCardProps {
  submissionId: string | null | undefined;
  handleNavigation: ISubmissionNavigator | null | undefined;
  renderError?: (error: ErrorType<unknown>) => React.ReactNode;
  readonly?: boolean;
  submission: RichiestaDTO | null | undefined;
  hideCard?: boolean;
  isBackoffice?: boolean;
}

/**
 * gestisce la risposta del pagamento
 */
function parsePaymentResponse(location: string) {
  const operationResponse = qs.parse(location, {
    ignoreQueryPrefix: true,
  });

  // gestione caso particolare: esito bollo è impostato a "successo" ma esito è impostato a "ERROR"
  // "successo" è il valore che impostiamo di default in `urlRitorno`
  if (
    String(operationResponse.esitoBollo).toLowerCase() == 'error' ||
    String(operationResponse.esito).toLowerCase() == 'error'
  ) {
    return 'error';
  }

  const paymentResponseStatus =
    operationResponse.esitoBollo ?? operationResponse.esito;

  if (Array.isArray(paymentResponseStatus)) {
    return null;
  }

  const paymentResponseStatusLower = String(
    paymentResponseStatus,
  ).toLowerCase();

  if (
    paymentResponseStatusLower === 'successo' ||
    paymentResponseStatusLower === 'success' ||
    paymentResponseStatusLower === 'OK'
  ) {
    return 'success';
  }

  if (paymentResponseStatusLower === 'indietro') {
    return 'back';
  }

  return null;
}

export function SubmissionPaymentsCard(props: SubmissionPaymentsCardProps) {
  const {
    submissionId,
    handleNavigation,
    renderError,
    readonly = false,
    submission,
    hideCard = false,
    isBackoffice = false,
  } = props;

  const location = useLocation();
  const { t } = useTranslation();

  const paymentResponseStatus = useMemo(
    () => parsePaymentResponse(location.search),
    [location.search],
  );

  useEffect(() => {
    if (paymentResponseStatus === 'success') {
      message.success(
        t(
          'submission-payment-form.pagamento-completato-con-successo',
          'Pagamento completato con successo',
        ),
      );
    }
    if (paymentResponseStatus === 'back') {
      message.info(
        t('submission-payment-form.pagamento-annullato', 'Pagamento annullato'),
      );
    }
    if (paymentResponseStatus === 'error') {
      message.error(
        t(
          'submission-payment-form.pagamento-errore',
          'Si è verificato un errore durante il pagamento',
        ),
      );
    }
  }, [paymentResponseStatus, t]);

  const burdensQuery = useGetFrontRichiesteIdOneri(
    submissionId ?? '<unknown>',
    { query: { keepPreviousData: true, enabled: Boolean(submissionId) } },
  );

  const paymentsQuery = useGetFrontRichiesteIdPagamenti(
    submissionId ?? '<unknown>',
    {
      query: {
        enabled: Boolean(submissionId),
        keepPreviousData: true,
        // retryDelay: 5000,
        refetchInterval: (pagamenti, query) => {
          if (!pagamenti) {
            // query.options.retry = false;
            return false;
          }
          // query.options.retry = true;
          return getValidPayments(pagamenti).some(
            p => p.codStatoPagamento === 'PENDENTE',
          )
            ? 5000
            : false;
        },
      },
    },
  );

  const burdens = useMemo(
    () => burdensQuery.data?.filter(b => !b.tipoOnere?.flPagamentoBollo) ?? [],
    [burdensQuery.data],
  );
  const stampDutyBurdens = useMemo(
    () => burdensQuery.data?.filter(b => b.tipoOnere?.flPagamentoBollo) ?? [],
    [burdensQuery.data],
  );
  const payments = useMemo(
    () => getValidPayments(paymentsQuery.data),
    [paymentsQuery.data],
  );

  // const quotes = fixuturesPreventivi;
  const quotesResult = useGetFrontRichiesteIdPreventivi(
    submissionId ?? '<unknown>',
    { query: { keepPreviousData: true, enabled: Boolean(submissionId) } },
  );

  const quotes = useMemo(
    () => quotesResult.data ?? [],
    [quotesResult.data],
  ) as ISubmissionQuote[];

  const isAllAfterSendBurdens =
    burdens.length &&
    burdens.every(
      b =>
        b.tipoOnere?.tipoVincolo ===
        EnumTipoOnereDTOTipoVincolo.DA_PAGARE_DOPO_INVIO,
    );
  const isAllAfterSendStampDutyBurdens =
    burdens.length &&
    stampDutyBurdens.every(
      b =>
        b.tipoOnere?.tipoVincolo ===
        EnumTipoOnereDTOTipoVincolo.DA_PAGARE_DOPO_INVIO,
    );
  const hidePayments = isAllAfterSendBurdens && isAllAfterSendStampDutyBurdens;

  const burdensHelpText = isAllAfterSendBurdens
    ? t(
        'submission-payment-form.oneri-bolli-da-pagare-dopo-invio',
        'Di seguito trovi i pagamenti da effettuare a seguito dell’invio',
      )
    : t(
        'submission-payment-form.seleziona-oneri',
        'Seleziona gli oneri per procedere al pagamento',
      );

  const stampDutyHelpText = isAllAfterSendStampDutyBurdens
    ? t(
        'submission-payment-form.oneri-bolli-da-pagare-dopo-invio',
        'Di seguito trovi i pagamenti da effettuare a seguito dell’invio',
      )
    : t(
        'submission-payment-form.seleziona-bolli',
        'Seleziona i bolli per procedere al pagamento',
      );

  // Richiamo il custom hook per la generazione automatica dei pagamenti dopo l'invio
  const automaticBurdenLoading = useGenerateAutomaticBurdenPayments(
    'beforeSent',
    submissionId,
    submission?.stato != EnumRichiestaDTOStato.BOZZA,
  );

  if (
    burdensQuery.isLoading ||
    paymentsQuery.isLoading ||
    quotesResult.isLoading ||
    automaticBurdenLoading
  ) {
    return <LoaderSpin transparent full />;
  }

  if (burdensQuery.isError || paymentsQuery.isError || quotesResult.isError) {
    return (
      renderError?.(
        (burdensQuery.error ?? paymentsQuery.error ?? quotesResult.error)!,
      ) ?? null
    );
  }

  if (!submission) {
    return (
      <CompilationContainer>
        <Alert
          type="error"
          showIcon
          message={t(
            'submission-payment-form.errore-richiesta-titolo',
            'Richiesta non definita',
          )}
          description={t(
            'submission-payment-form.errore-richiesta',
            'Si è verificato un errore durante il caricamento della richiesta. La richiesta non è definita.',
          )}
          style={{ marginBottom: 24 }}
        />
      </CompilationContainer>
    );
  }

  return (
    <>
      <CompilationContainer>
        {paymentResponseStatus === 'success' && (
          <Alert
            type="success"
            showIcon
            message={t(
              'submission-payment-form.pagamento-completato',
              'Il pagamento è stato completato con successo',
            )}
            style={{ marginBottom: 24 }}
          />
        )}

        {paymentResponseStatus === 'error' && (
          <Alert
            type="error"
            showIcon
            message={t(
              'submission-payment-form.pagamento-errore',
              'Si è verificato un errore durante il pagamento',
            )}
            style={{ marginBottom: 24 }}
          />
        )}

        <StyledResponsiveCardWrapper hideCard={hideCard}>
          {quotes.length > 0 && (
            <div>
              <Title level={3}>
                {t('submission-menu-item.preventivi', 'Preventivi')}
              </Title>
              <QuoteTable
                readonly={readonly}
                quotes={quotes}
                submissionId={submissionId}
              />
              <Divider />
            </div>
          )}
          {burdens.length > 0 && (
            <>
              <Title level={3}>Oneri</Title>
              {!readonly && <Paragraph>{burdensHelpText}</Paragraph>}
              <BurdenSelectionTable burdens={burdens} readonly={readonly} />
            </>
          )}

          {stampDutyBurdens.length > 0 && (
            <>
              {burdens.length > 0 && <Divider />}
              <Title level={3}>
                {t('submission-menu-item.bolli', 'Bolli')}
              </Title>
              {!readonly && <Paragraph>{stampDutyHelpText}</Paragraph>}
              <BurdenSelectionTable
                burdens={stampDutyBurdens}
                readonly={readonly}
              />
            </>
          )}

          {/* {isAllAfterSendBurdens && isAllAfterSendStampDutyBurdens ? (
            <div>
              <Divider />
              <Title level={3}>
                {t('frontoffice-submission:submission-menu-item.pagamenti', 'Pagamenti')}
              </Title>
              <Paragraph>
                {t(
                  'front:submission-form.inviare-per-procedere-al-pagamento',
                  "Procedere all'invio della Richiesta per effettuare il pagamento degli oneri.",
                )}
              </Paragraph>
            </div>
          ) : ( */}
          {!hidePayments && (
            <div>
              <Divider />

              <Title level={3}>
                {t('submission-menu-item.pagamenti', 'Pagamenti')}
              </Title>
              <PaymentsTable
                payments={payments}
                readonly={readonly}
                submission={submission}
              />
            </div>
          )}
        </StyledResponsiveCardWrapper>
      </CompilationContainer>

      {handleNavigation && (
        <SubmissionBurdenCardFooter
          handleNavigation={handleNavigation}
          readonly={readonly}
          isBackoffice={isBackoffice}
        />
      )}
    </>
  );
}
